import type { SafetyDashboardRow } from '../../services/reportsApi';
import { buildScanVerifyPath } from '../acceptance/scanVerifyNavigation';

const CURRENT_SCAN_REVIEW_STATUSES = new Set(['draft', 'in_review']);
const SCAN_VERIFY_PATH_RE = /^\/acceptance\/scans\/([^/]+)\/verify$/;

/**
 * Current Scan = the unsigned draft operators review and sign.
 * Signed / finalized reports are frozen Records, not the episode-review queue.
 */
export function resolveCurrentScanReportId(row?: {
  active_draft_report_id?: string | null;
  report_id?: string | null;
  report_status?: string | null;
  latest_scan_id?: string | null;
  latest_scan_status?: string | null;
} | null): string {
  const draftId = String(row?.active_draft_report_id || '').trim();
  if (draftId) return draftId;
  const status = String(row?.report_status || '').trim().toLowerCase();
  if (CURRENT_SCAN_REVIEW_STATUSES.has(status)) return String(row?.report_id || '').trim();
  const latestStatus = String(row?.latest_scan_status || '').trim().toLowerCase();
  if (CURRENT_SCAN_REVIEW_STATUSES.has(latestStatus)) {
    return String(row?.latest_scan_id || '').trim();
  }
  return '';
}

export function resolveCanonicalLinkedSafetyCaseId(args: {
  safetyCaseId?: string | null;
  dashboardRow?: Pick<SafetyDashboardRow, 'safety_case_id' | 'finding_id'> | null;
}): string {
  return String(
    args.safetyCaseId
    || args.dashboardRow?.safety_case_id
    || args.dashboardRow?.finding_id
    || '',
  ).trim();
}

/**
 * Pattern review CTA destination when a linked Safety Case already has a current Scan.
 * Same route as Safety Case "Review N episodes": /acceptance/scans/{reportId}/verify.
 * Null when Pattern is still establishing (no Case) or no current Scan exists —
 * callers must send Case-without-Scan to the Case, and no-Case to Pattern evidence.
 */
export function buildCurrentScanReviewPath(args: {
  dashboardRow?: SafetyDashboardRow | null;
  safetyCaseId?: string | null;
  orgId?: string | null;
  siteId?: string | null;
  workflowId?: string | null;
  scanScopeStart?: string | null;
  scanScopeEnd?: string | null;
}): string | null {
  const row = args.dashboardRow;
  if (!row) return null;
  const safetyCaseId = resolveCanonicalLinkedSafetyCaseId({
    safetyCaseId: args.safetyCaseId,
    dashboardRow: row,
  });
  if (!safetyCaseId) return null;
  const reportId = resolveCurrentScanReportId(row);
  if (!reportId) return null;

  const path = buildScanVerifyPath({
    reportId,
    orgId: args.orgId,
    siteId: args.siteId || row.site_id,
    safetyCaseId,
  });
  return String(path || '').trim() || null;
}

export function isScanReviewDeepLink(path?: string | null): boolean {
  const value = String(path || '').trim();
  if (!value) return false;
  const [pathname, queryString] = value.split('?');
  const match = SCAN_VERIFY_PATH_RE.exec(pathname || '');
  if (!match) return false;
  const reportId = decodeURIComponent(match[1] || '').trim();
  const query = new URLSearchParams(queryString || '');
  return Boolean(reportId && (query.get('safety_case_id') || query.get('case_id')));
}

export type PatternReviewScanCtaActions = {
  onReviewEvidence?: () => void;
  onContinueReview?: () => void;
  onReviewNewActivity?: () => void;
  onCreateSafetyCase?: () => void;
  onOpenSafetyCase?: () => void;
  currentScanReviewPath?: string | null;
  linkedSafetyCasePath?: string | null;
};

export function readCurrentScanReviewPath(
  actions?: { currentScanReviewPath?: string | null } | null,
): string | null {
  return String(actions?.currentScanReviewPath || '').trim() || null;
}

export function readPatternReviewPrimaryHref(actions?: {
  currentScanReviewPath?: string | null;
  linkedSafetyCasePath?: string | null;
} | null): string | null {
  return String(actions?.currentScanReviewPath || actions?.linkedSafetyCasePath || '').trim() || null;
}

export function omitDuplicatePatternReviewCtas<T extends PatternReviewScanCtaActions>(
  actions: T | undefined,
  nextRequiredAction?: string | null,
): T | undefined {
  if (!actions) return undefined;
  if (nextRequiredAction !== 'review_new_activity' && nextRequiredAction !== 'review_evidence') {
    return actions;
  }
  return {
    ...actions,
    onReviewNewActivity: undefined,
    onContinueReview: undefined,
    onReviewEvidence: undefined,
  };
}

export function bindPatternReviewActionsToScanOrEvidence<T extends PatternReviewScanCtaActions>(
  actions: T | undefined,
  currentScanReviewPath: string | null,
  markEvidenceFocus: () => void,
): T | undefined {
  if (!actions) return undefined;
  if (currentScanReviewPath || actions.linkedSafetyCasePath) return actions;
  const wrap = (action?: () => void) => (
    action
      ? () => {
          markEvidenceFocus();
          action();
        }
      : undefined
  );
  return {
    ...actions,
    onReviewEvidence: wrap(actions.onReviewEvidence),
    onContinueReview: wrap(actions.onContinueReview),
    onReviewNewActivity: wrap(actions.onReviewNewActivity),
  };
}

export function resolvePatternReviewPrimaryHref(
  action?: string | null,
  currentScanReviewPath?: string | null,
): string | null {
  if (action === 'review_new_activity' || action === 'review_evidence') {
    return currentScanReviewPath || null;
  }
  return null;
}

export function resolvePatternReviewPrimaryHandler(args: {
  action?: string | null;
  currentScanReviewPath?: string | null;
  actions?: PatternReviewScanCtaActions;
  onVerifyObservations?: () => void;
  onScrollPatternEvidence?: () => void;
}): (() => void) | undefined {
  const action = args.action;
  const actions = args.actions;
  if (action === 'create_safety_case') return actions?.onCreateSafetyCase;
  if (action === 'open_safety_case') return actions?.onOpenSafetyCase;
  if (action === 'verify_observations') return args.onVerifyObservations;
  if (action === 'review_new_activity') {
    return actions?.onReviewNewActivity || args.onScrollPatternEvidence;
  }
  if (action === 'review_evidence') {
    if (args.currentScanReviewPath) {
      return actions?.onContinueReview || actions?.onReviewEvidence || actions?.onReviewNewActivity;
    }
    return args.onScrollPatternEvidence;
  }
  return undefined;
}
